import React, { useCallback, useEffect, useState } from "react";
import GenericTable from "../GenericTable/GenericTable";
import config from "../../../config";
import axios from "axios";
import { Box, CircularProgress, TextField, Button, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup, FormControlLabel, Radio } from "@mui/material";

// ✅ FIXED: Client Order List with Filters (v2)

const formatValue = (value) =>
  value === null || value === undefined || value === "" ? "N/A" : value;

// ✅ Format date from ISO format (2025-12-02T00:00:00) to MM-DD-YYYY HH:MM:SS
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${month}-${day}-${year} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    return "N/A";
  }
};

const orderColumns = [
  { id: "order_id", label: "Order ID", render: (row) => formatValue(row.order_id) },
  { id: "order_date", label: "Order Date", render: (row) => formatDate(row.order_date) },
  { id: "client_name", label: "Client Name", render: (row) => formatValue(row.client_name) },
  { id: "store_name", label: "Store Name", render: (row) => formatValue(row.store_name) },
  { id: "customer_name", label: "Customer Name", render: (row) => formatValue(row.customer_name) },
  {
    id: "products",
    label: "Product Name(s)",
    render: (row) => Array.isArray(row.products) ? row.products.join(", ") : "N/A",
  },
  {
    id: "order_amount",
    label: "Order Amount",
    render: (row) => row.order_amount ? `$${Number(row.order_amount).toLocaleString()}` : "N/A",
  },
  {
    id: "budi_amount_raw",
    label: "Bud-i Amount($)",
    render: (row) => row.budi_amount ? `$${Number(row.budi_amount).toLocaleString()}` : "N/A",
  },
];

function ClientOrderList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // ✅ FILTER STATES
  const [search, setSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedStore, setSelectedStore] = useState("");

  // ✅ DROPDOWN OPTIONS
  const [clients, setClients] = useState([]);
  const [stores, setStores] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  // ✅ EXPORT STATES
  const [exportOpen, setExportOpen] = useState(false);
  const [exportType, setExportType] = useState("csv");

  // ✅ FETCH FILTER OPTIONS (Clients & Stores) - Only once on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      setDropdownLoading(true);
      try {
        const res = await axios.get(
          `${config.API_BASE_URL_V1}/admin/client-orders/filter-options`
        );
        if (res.data.success) {
          setClients(res.data.data.clients || []);
          setStores(res.data.data.stores || []);
        }
      } catch (error) {
        console.error("Error fetching filter options:", error);
      }
      setDropdownLoading(false);
    };
    fetchFilterOptions();
  }, []);

  // ✅ UNIVERSAL FETCH: Server-side pagination with filters
  const fetchOrders = useCallback(async (pageNum = page, rowSize = rowsPerPage, filters = {}) => {
    setLoading(true);
    try {
      const queryParams = {
        page: pageNum,
        limit: rowSize,
        search: filters.search !== undefined ? filters.search : search,
        client_name: filters.client_name !== undefined ? filters.client_name : selectedClient,
        store_name: filters.store_name !== undefined ? filters.store_name : selectedStore,
      };

      // Remove undefined/empty values
      Object.keys(queryParams).forEach(
        (key) => !queryParams[key] && delete queryParams[key]
      );

      const res = await axios.get(
        `${config.API_BASE_URL_V1}/admin/client-orders`,
        { params: queryParams }
      );

      setRows(res.data.data);
      setTotalCount(res.data?.pagination?.total_records || 0);
    } catch (error) {
      console.error("API Error:", error);
      setRows([]);
    }
    setLoading(false);
  }, [page, rowsPerPage, search, selectedClient, selectedStore]);

  // ✅ AUTO-FETCH: When page or rowsPerPage changes (keeps current filters)
  useEffect(() => {
    fetchOrders(page, rowsPerPage, {
      search: search,
      client_name: selectedClient,
      store_name: selectedStore,
    });
  }, [page, rowsPerPage]);

  // ✅ HANDLE SEARCH: Reset to page 1 and fetch with filters
  const handleSearch = () => {
    setPage(1);
    fetchOrders(1, rowsPerPage, {
      search: search,
      client_name: selectedClient,
      store_name: selectedStore,
    });
  };

  // ✅ CLEAR FILTERS: Reset all and fetch fresh
  const handleClearFilters = () => {
    setSearch("");
    setSelectedClient("");
    setSelectedStore("");
    setPage(1);
    fetchOrders(1, rowsPerPage, {
      search: "",
      client_name: "",
      store_name: "",
    });
  };

  // ✅ EXPORT: Get ALL filtered results (ignores pagination)
  const handleExportData = async () => {
    try {
      setLoading(true);
      let allData = [];
      let currentPage = 1;
      let hasMore = true;

      // ✅ Fetch ALL pages until no more data
      while (hasMore) {
        const res = await axios.get(`${config.API_BASE_URL_V1}/admin/client-orders`, {
          params: {
            page: currentPage,
            limit: 100, // Backend max limit is 100
            search: search || undefined,
            client_name: selectedClient || undefined,
            store_name: selectedStore || undefined,
          },
        });

        const pageData = res.data?.data || [];
        const totalRecords = res.data?.pagination?.total_records || 0;

        if (pageData.length === 0) {
          hasMore = false;
        } else {
          allData = [...allData, ...pageData];
          currentPage++;
        }
      }

      // Generate CSV
      const headers = [
        "Order ID",
        "Order Date",
        "Client Name",
        "Store Name",
        "Customer Name",
        "Products",
        "Order Amount",
        "Bud-i Amount",
      ];

      const csvContent = [
        headers.join(","),
        ...allData.map((row) =>
          [
            row.order_id,
            formatDate(row.order_date),
            row.client_name,
            row.store_name,
            row.customer_name || "N/A",
            `"${Array.isArray(row.products) ? row.products.join("; ") : ""}"`,
            row.order_amount,
            row.budi_amount,
          ].join(",")
        ),
      ].join("\n");

      // Download CSV
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `client_orders_export_${new Date().getTime()}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      setExportOpen(false);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* ✅ FILTER BAR - WHITE BACKGROUND */}
      <Box sx={{ mb: 0, p: 2, backgroundColor: "#ffffff", borderRadius: "8px 8px 0 0", display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", border: "1px solid #e0e0e0", borderBottom: "none" }}>
        <TextField
          label="Search Order ID"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search order..."
          sx={{ minWidth: 200 }}
        />

        <TextField
          select
          label="Client Name"
          size="small"
          value={selectedClient}
          onChange={(e) => setSelectedClient(e.target.value)}
          sx={{ minWidth: 200 }}
          disabled={dropdownLoading}
        >
          <MenuItem value="">-- Select Client --</MenuItem>
          {clients.map((client) => (
            <MenuItem key={client.id} value={client.name}>
              {client.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Store Name"
          size="small"
          value={selectedStore}
          onChange={(e) => setSelectedStore(e.target.value)}
          sx={{ minWidth: 200 }}
          disabled={dropdownLoading}
        >
          <MenuItem value="">-- Select Store --</MenuItem>
          {stores.map((store) => (
            <MenuItem key={store.id} value={store.name}>
              {store.name}
            </MenuItem>
          ))}
        </TextField>

        <Button
          variant="contained"
          onClick={handleSearch}
          sx={{ height: "40px", backgroundColor: "#1976d2", color: "#fff", "&:hover": { backgroundColor: "#1565c0" } }}
        >
          Search
        </Button>

        <Button
          variant="outlined"
          onClick={handleClearFilters}
          sx={{ height: "40px", borderColor: "#1976d2", color: "#1976d2", "&:hover": { backgroundColor: "#f5f5f5" } }}
        >
          Clear Filters
        </Button>

        <Button
          variant="contained"
          onClick={() => setExportOpen(true)}
          sx={{ height: "40px", backgroundColor: "#1976d2", color: "#fff", "&:hover": { backgroundColor: "#1565c0" } }}
        >
          Export
        </Button>
      </Box>

      {/* ✅ EXPORT DIALOG */}
      <Dialog open={exportOpen} onClose={() => setExportOpen(false)}>
        <DialogTitle>Export Client Orders</DialogTitle>
        <DialogContent>
          <RadioGroup
            value={exportType}
            onChange={(e) => setExportType(e.target.value)}
            sx={{ marginTop: "16px" }}
          >
            <FormControlLabel value="csv" control={<Radio />} label="CSV" />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportOpen(false)}>Cancel</Button>
          <Button onClick={handleExportData} variant="contained">
            Export
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ TABLE WITH SERVER-SIDE PAGINATION */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <CircularProgress />
        </Box>
      )}
      {!loading && (
        <GenericTable
          title="" // ✅ REMOVED: Client Order heading
          columns={orderColumns}
          rows={rows}
          showView={false}
          showEdit={false}
          showDelete={false}
          hideExport={true} // ✅ Hide export button - using custom export in filter bar
          isServerSide={true}
          currentPage={page - 1}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={(_, newPage) => {
            setPage(newPage + 1);
          }}
          onRowsPerPageChange={(event) => {
            const newRowsPerPage = parseInt(event.target.value, 10);
            setRowsPerPage(newRowsPerPage);
            setPage(1);
          }}
        />
      )}
    </div>
  );
}

export default ClientOrderList;

// import React from "react";
// import GenericTable from "../GenericTable/GenericTable";
// import revenueList from "../../../data/revenue_overview_success.json"; // Assuming you have a JSON file with user data
// const revenueColumns = [
//   { id: "order_id", label: "Order ID" },
//   { id: "client_name", label: "Client Name"},
//   { id: "customer_name", label: "Customer Name" },
//   {
//     id: "product_names",
//     label: "Product Name(s)",
//     render: (row) => {
//       if (Array.isArray(row.product_names)) {
//         return row.product_names.join(", ");
//       }
//       return row.product_names || "";
//     }
//   },
//   {
//   id: "order_amount",
//   label: "Order Amount",
//   render: (row) => `$ ${Number(row.order_amount).toLocaleString()}`
// },
//  {
//   id: "bude_order_amount",
//   label: "Liza Amount($)",
//   render: (row) => `$ ${Number(row.bude_order_amount).toLocaleString()}`
// } ,
//   { id: "store_name", label: "Store Name" },
//   { id: "order_date", label: "Order Date" }
// ];

// function RevenueList() {
//   return (
//  <GenericTable
//       title="Client's Order"
//       columns={revenueColumns}
//       rows={revenueList.data}
//       showView={false}
//       showEdit={false}
//       showDelete={false}
//       dropdownFilters={["client_name","store_name"]}
//       rangeFilters={["order_amount"]}
//       // dateFilters={["order_date"]}
//       onView={(row) => alert(`View user: ${row.username}`)}
//       onEdit={(row) => alert(`Edit user: ${row.username}`)}
//       onDelete={(row) => {
//         if (window.confirm(`Delete user: ${row.username}?`)) {
//           console.log("Deleted", row);
//         }
//       }}
//     />
//   );
// }

// export default RevenueList;



// import React from "react";
// import GenericTable from "../GenericTable/GenericTable";
// import orderData from "../../../data/revenue_overview_success.json";
 
// // ========= COLUMN MAPPING =========
// const orderColumns = [
//   { id: "order_id", label: "Order ID" },
//   { id: "order_date", label: "Order Date" },
//   { id: "client_name", label: "Client Name" },
//   { id: "store_name", label: "Store Name" },
//   { id: "customer_name", label: "Customer Name" },
//   {
//     id: "product_names",
//     label: "Product Name(s)",
//     render: (row) =>
//       Array.isArray(row.product_names)
//         ? row.product_names.join(", ")
//         : row.product_names || "",
//   },
//   {
//     id: "order_amount",
//     label: "Order Amount",
//     render: (row) => `$ ${Number(row.order_amount).toLocaleString()}`,
//   },
//   {
//     id: "bude_order_amount",
//     label: "Bud-i Amount($)",
//     render: (row) => `$ ${Number(row.bude_order_amount).toLocaleString()}`,
//   },
// ];
 
// function ClientOrderList() {
//   return (
//     <GenericTable
//       title="Client Order"
//       columns={orderColumns}
//       rows={orderData.data}
 
//       /* disable all default actions */
//       showView={false}
//       showEdit={false}
//       showDelete={false}
 
//       /* Filters required */
//       dateFilters={["order_date"]}              // Date range filter
//       rangeFilters={["bude_order_amount"]}      // Bud-i Amount filter
 
//       /* removed client + store dropdown filters */
//       dropdownFilters={[]}                      // 👈 Removed here
//     />
//   );
// }
 
// export default ClientOrderList;
 